import { ChangeEvent, useState } from 'react';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Popover,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import DialogTitleWithX from '@/components/DialogTitleWithX';
import { getBorder } from '@/theme/utils';
import { DialogProps } from '@toolpad/core/useDialogs/useDialogs';
import EmojiPicker from 'emoji-picker-react';
import { useSnackbar } from 'notistack';

import { useSelectedTeamId } from '../hooks';
import { getTeamNameErrors } from '../utils';
import useCreateTeam from './hooks';
import { EmojiPickerContainer } from './styled';

const DEFAULT_TEAM_ICON_CHOICES = [
  '🚀',
  '✨',
  '🧠',
  '🌟',
  '⚡️',
  '🛠️',
  '🧪',
  '📈',
  '🎯',
  '🤖',
  '🛰️',
  '🔮',
  '🧩',
  '🪐',
  '🎛️',
  '📡',
  '⚙️',
  '🧬',
  '🧭',
  '🪄',
  '🛡️',
  '🧰',
  '🚦',
  '📊',
];

function getDefaultRandomTeamIcon() {
  const index = Math.floor(Math.random() * DEFAULT_TEAM_ICON_CHOICES.length);
  return DEFAULT_TEAM_ICON_CHOICES[index] ?? '🚀';
}

function CreateTeam({ open, onClose }: DialogProps) {
  const [name, setName] = useState('');
  const { createTeam, loading } = useCreateTeam();
  const { setTeamId } = useSelectedTeamId();
  const { enqueueSnackbar } = useSnackbar();
  const [selectedEmoji, setSelectedEmoji] = useState<string>(() => getDefaultRandomTeamIcon());
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const errors = getTeamNameErrors(name);

  function handleNameChange(ev: ChangeEvent<HTMLInputElement>) {
    setName(ev.target.value);
  }

  async function createNewTeam() {
    try {
      const { data } = await createTeam({ variables: { name, icon: selectedEmoji } });
      enqueueSnackbar('Team created successfully', { variant: 'success' });
      setTeamId(data.createTeam.id);
      onClose();
    } catch (error) {
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  }

  return (
    <Dialog
      open={open}
      onClose={() => onClose()}
      slotProps={{
        paper: {
          sx: { width: 500 },
        },
      }}
    >
      <DialogTitleWithX
        onClose={onClose}
        title="Create a new team"
        sx={{
          pb: 2,
          borderBottom: (theme) => getBorder(theme.palette.border.default),
          paddingBottom: (theme) => theme.spacing(2),
        }}
      >
        Create a new team
      </DialogTitleWithX>
      <DialogContent sx={{ mt: 2 }}>
        <Stack gap={2} direction="row" alignItems="flex-start">
          <Stack gap={1}>
            <Typography variant="h5">Icon</Typography>
            <Button variant="outlined" onClick={(ev) => setAnchorEl(ev.currentTarget)}>
              {selectedEmoji || 'Select icon'}
            </Button>
          </Stack>
          <Stack gap={1} flex={1}>
            <Stack gap={1} flex={1}>
              <Typography variant="h5">Name</Typography>
              <TextField
                autoFocus
                value={name}
                onChange={handleNameChange}
                placeholder="e.g. Research"
              />
            </Stack>
            {errors.length > 0 && (
              <Stack component="ul" m={0} pl={0.25} sx={{ listStyle: 'none' }}>
                {errors.map((error) => (
                  <Typography component="li" key={error} variant="h6" color="text.danger">
                    {error}
                  </Typography>
                ))}
              </Stack>
            )}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions
        sx={(theme) => ({
          background: theme.palette.background.subtle,
          borderTop: getBorder(theme.palette.border.default),
          justifyContent: 'space-between',
        })}
      >
        <Button variant="outlined" onClick={() => onClose()}>
          Cancel
        </Button>
        <Button
          loading={loading}
          disabled={!name || !!errors.length}
          onClick={createNewTeam}
          variant="contained"
          color="success"
        >
          Create team
        </Button>
      </DialogActions>
      <Popover open={!!anchorEl} anchorEl={anchorEl} onClose={() => setAnchorEl(null)}>
        <EmojiPickerContainer>
          <EmojiPicker
            onEmojiClick={({ emoji }) => {
              setSelectedEmoji(emoji);
              setAnchorEl(null);
            }}
            skinTonesDisabled
          />
        </EmojiPickerContainer>
      </Popover>
    </Dialog>
  );
}

export default CreateTeam;
